// MultipartOGSModel — the multi-region OGS splat avatar, with its body blueprint driving
// the part rig. A WRAPPER, so a myra change can't reach OGSModel's other bundles; the
// drive it needs is already public (partDriveRegistry). Inert by data elsewhere.
// Notes: ~/comments/aos-threejs-poc/MultipartOGSModel_BodyBlueprintDrive_wrapper_and_attach.md

import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import OGSModel from './OGSModel'
import { graphState } from '../lib/graphState.js'
import { evaluateGraph, updateSMTransitions } from '../lib/GraphEvaluator.js'
import { debugState } from '../lib/debugState.js'
import { partDriveRegistry } from '../ogs/preview/partDriveRegistry'
import { listAnimationAssets } from '../lib/assetManagerApi.js'
import { resolveCharacterId } from '../lib/characterState.js'
import { setLiveBodyRig, isLiveBodyRigWanted } from '../lib/liveBodyRig.js'
import { gestureEnvelope, stopBodyGesture } from '../lib/GestureChannel.js'
import { movement } from '../lib/movementComponent'
import { resolveAvatarTransformTarget } from '../lib/avatarTransformTarget'
import { setVariable } from '../lib/animVariables'
import {
  loadBodyClipRows, isGraphAuthored, createPartRigDriver,
} from '../ogs/inference/multipartBodyDrive.js'

function BodyBlueprintDrive() {
  // Not React state: a setState here would re-render the scene on the hot path.
  const driverRef = useRef(null)
  const rigRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    let attaching = false // the poll re-fires mid-attach and would re-download every clip
    // A preview asks for the drive as much as an authored graph does — and so does a
    // generated clip, which cannot arm __DIRECT_PLAY__ first because it has to BAKE
    // against this rig to exist at all (liveBodyRig.ensureLiveBodyRig).
    const wanted = () => isGraphAuthored(graphState) || !!window.__DIRECT_PLAY__?.clipName
      || isLiveBodyRigWanted()
    const attach = async () => {
      if (attaching || !partDriveRegistry.isAvailable() || !wanted()) return false
      attaching = true
      try {
        const drive = await partDriveRegistry.build()
        if (cancelled) return false
        // Null is a build that FAILED (the cause is already on the console), not "not yet"
        // — retrying would download the rig again every tick, forever.
        if (!drive) throw new Error('the part drive did not build — see the [ogs] error above')
        const { rig } = drive
        const clips = await loadBodyClipRows(resolveCharacterId(), listAnimationAssets)
        if (cancelled) return false
        const added = rig.addClips(await loadClipAnimations(clips))
        if (cancelled) return false
        rigRef.current = rig
        driverRef.current = createPartRigDriver({ rig, drive, evaluate: evaluateBodyGraph })
        // Generated clips bake against THIS rig, so they register on it.
        setLiveBodyRig({
          scene: rig.scenes[0],
          restPose: rig.restPose,
          // The frame this skeleton renders THROUGH — see ogs/inference/rigRenderFrame.js.
          rigToWorld: drive.rigToWorld ?? null,
          // And its SCALE — metres per rig unit. rig.glb is authored in CENTIMETRES and
          // nothing above `pelvis` says so, so a clip's world-metre root translation has
          // to be converted explicitly or a jump lands 100x too small (the GLB avatar
          // gets the same factor for free, off its Armature's 0.01).
          rigToWorldScale: drive.rigToWorldScale ?? null,
          // replace: one gesture name re-serves with new tracks every generation.
          // The CLIP says whether its pelvis track is travel or bob (buildBodyMotionClip
          // stamps it): an extracted walk's is the residual sway and the vertical, and
          // stripping that is what made a walk here glide flat — the body riding rigidly
          // on a smooth root with no bob and no weight shift. A clip that says nothing
          // keeps the old default, since its pelvis track would be raw travel and this
          // rig's root carries that.
          // `_name` rides on the clip; the arg exists for the GLB registrar's signature.
          // A caller that KNOWS the provenance states it — including `null`, which is the
          // GLB path's "keep the pelvis track" default and what an uploaded asset needs:
          // nothing else carries its travel, and after a reload loadClipAnimations binds
          // it exactly that way. The fallback below is for a LIVE generation, whose
          // travel is already on the avatar root.
          register: (_name, clip, { additive = false, generatedMarker } = {}) =>
            rig.addClips([clip], {
              additiveInPlace: additive,
              replace: true,
              generatedMarker: generatedMarker !== undefined ? generatedMarker
                : (clip.userData?.aosGeneratedMotion ?? { keepRootMotion: false }),
            }),
          clipDuration: (name) => rig.clipDuration(name),
        })
        console.info(`[multipart] body blueprint owns the part drive — ${added.length} clips: ${added.join(', ') || 'none'}`)
        return true
      } finally { attaching = false }
    }
    // The drive registers during OGSModel's boot, after this effect. Poll, don't race.
    const poll = setInterval(() => {
      attach().then((done) => { if (done) clearInterval(poll) }).catch((e) => {
        clearInterval(poll)
        console.error('[multipart] body drive failed to attach', e)
      })
    }, 250)
    return () => {
      cancelled = true
      clearInterval(poll)
      // A surviving handle would pose a disposed mixer on the next character's frame 1.
      driverRef.current = null
      rigRef.current = null
      setLiveBodyRig(null)
    }
  }, [])

  useEffect(() => {
    window.__MULTIPART_DRIVE__ = {
      get attached() { return !!rigRef.current },
      get owner() { return driverRef.current?.owner ?? 'none' },
      get clipNames() { return rigRef.current?.clipNames() ?? [] },
    }
    return () => { window.__MULTIPART_DRIVE__ = null }
  }, [])

  useFrame((_, delta) => {
    const driver = driverRef.current
    if (!driver) return
    const result = driver.frame({
      delta,
      now: Date.now(),
      graph: graphState,
      directPlay: window.__DIRECT_PLAY__,
      envelope: gestureEnvelope(),
    })
    // The root travels exactly as far as the clip has been posed — one clock for both.
    if (result.owner === 'override') window.__DIRECT_PLAY__?.rootMotion?.placeAt(result.progress)
    // Both windows are the component's: onExpire is the payload's, the channel a singleton.
    if (result.expiredDirectPlay) releaseDirectPlay(window.__DIRECT_PLAY__)
    if (result.clearGesture) stopBodyGesture()
  })

  return null
}

/** Evaluate the body graph and mirror its weights into debugState — editor parity, and
 *  BlueprintEditor's weight-bar poll is gated on the tick. */
function evaluateBodyGraph() {
  updateSMTransitions(graphState)
  const out = evaluateGraph(graphState)
  debugState.bodyWeights = out.weights ?? {}
  debugState.clipWeights = out.weights ?? {}
  debugState.tick++
  return out
}

/** Clear an elapsed override, running its release hook — the GLB path's contract. */
function releaseDirectPlay(directPlay) {
  window.__DIRECT_PLAY__ = null
  if (typeof directPlay?.onExpire !== 'function') return
  try { directPlay.onExpire() } catch (e) { console.warn('[multipart] DIRECT_PLAY onExpire threw:', e) }
}

/** Point movement at the splat root and publish its ground speed for the graph.
 *  Resolved per frame: the root publishes during OGSModel's boot, after any effect. */
function SplatMovementTarget() {
  useEffect(() => {
    movement.setSpeedSink((speed) => setVariable('Speed', speed))
    return () => {
      movement.setSpeedSink(null)
      setVariable('Speed', 0)
      if (movement.getGroup() === window.__OGS_AVATAR_ROOT__) movement.setGroup(null)
    }
  }, [])

  useFrame(() => {
    const target = resolveAvatarTransformTarget()
    if (target !== movement.getGroup()) movement.setGroup(target)
  })

  return null
}

/** Fetch each row's GLB and take its one clip, renamed to the row's name — a GLB's own
 *  clip name is not reliably it. A failed load is dropped and named, never substituted. */
async function loadClipAnimations(rows) {
  const loader = new GLTFLoader()
  const loaded = await Promise.all(rows.map((row) => new Promise((resolve) => {
    loader.load(row.url, (gltf) => {
      const clip = gltf.animations?.[0]
      if (!clip) {
        console.warn(`[multipart] '${row.name}' carries no animation clip — skipped`)
        return resolve(null)
      }
      clip.name = row.name
      // Carried so the rig prunes it the way the GLB avatar does.
      const marker = gltf.scene?.userData?.aosGeneratedMotion
      if (marker) clip.userData = { ...clip.userData, aosGeneratedMotion: marker }
      resolve(clip)
    }, undefined, (e) => {
      console.warn(`[multipart] '${row.name}' failed to load — skipped: ${e?.message ?? e}`)
      resolve(null)
    })
  })))
  return loaded.filter(Boolean)
}

export default function MultipartOGSModel(props) {
  return (
    <>
      <OGSModel {...props} />
      <BodyBlueprintDrive />
      <SplatMovementTarget />
    </>
  )
}
